"use client";
import { cn } from "@/lib/utils";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "next-intl";
import React from "react";

// ============================================================================
// Field type definitions + per-entity field sets
// ============================================================================

type FieldType =
  | "string"
  | "number"
  | "currency"
  | "boolean"
  | "date"
  | "single_select"
  | "multi_select"
  | "url"
  | "email"
  | "phone";

type FieldDef = {
  nameKey: string;
  type: FieldType;
  required?: boolean;
  optionKeys?: string[];
  optionAccents?: OptionAccent[];
};

type OptionAccent = "red" | "amber" | "emerald" | "sky" | "violet" | "neutral";

const ACCENT_CLASS: Record<OptionAccent, string> = {
  red: "bg-red-500/15 text-red-200 ring-red-500/30",
  amber: "bg-amber-500/15 text-amber-200 ring-amber-500/30",
  emerald: "bg-emerald-500/15 text-emerald-200 ring-emerald-500/30",
  sky: "bg-sky-500/15 text-sky-200 ring-sky-500/30",
  violet: "bg-violet-500/15 text-violet-200 ring-violet-500/30",
  neutral: "bg-neutral-800 text-neutral-300 ring-neutral-700",
};

type EntityKey =
  | "lead"
  | "contact"
  | "appointment"
  | "calldoc";

const ENTITY_KEYS: EntityKey[] = [
  "lead",
  "contact",
  "appointment",
  "calldoc",
];

const ENTITY_FIELDS: Record<EntityKey, FieldDef[]> = {
  lead: [
    {
      nameKey: "lead_f1_name",
      type: "single_select",
      required: true,
      optionKeys: ["lead_f1_o1", "lead_f1_o2", "lead_f1_o3"],
      optionAccents: ["red", "amber", "emerald"],
    },
    { nameKey: "lead_f2_name", type: "currency" },
    {
      nameKey: "lead_f3_name",
      type: "multi_select",
      optionKeys: ["lead_f3_o1", "lead_f3_o2", "lead_f3_o3"],
      optionAccents: ["sky", "violet", "neutral"],
    },
    { nameKey: "lead_f4_name", type: "string", required: true },
  ],
  contact: [
    { nameKey: "contact_f1_name", type: "url" },
    {
      nameKey: "contact_f2_name",
      type: "single_select",
      optionKeys: ["contact_f2_o1", "contact_f2_o2", "contact_f2_o3"],
      optionAccents: ["red", "amber", "sky"],
    },
    {
      nameKey: "contact_f3_name",
      type: "single_select",
      required: true,
      optionKeys: ["contact_f3_o1", "contact_f3_o2", "contact_f3_o3"],
      optionAccents: ["red", "amber", "neutral"],
    },
    {
      nameKey: "contact_f4_name",
      type: "multi_select",
      optionKeys: ["contact_f4_o1", "contact_f4_o2", "contact_f4_o3"],
      optionAccents: ["red", "sky", "violet"],
    },
  ],
  appointment: [
    {
      nameKey: "appointment_f1_name",
      type: "single_select",
      required: true,
      optionKeys: [
        "appointment_f1_o1",
        "appointment_f1_o2",
        "appointment_f1_o3",
      ],
      optionAccents: ["red", "amber", "emerald"],
    },
    { nameKey: "appointment_f2_name", type: "number" },
    { nameKey: "appointment_f3_name", type: "url" },
  ],
  calldoc: [
    { nameKey: "calldoc_f1_name", type: "boolean" },
    {
      nameKey: "calldoc_f2_name",
      type: "single_select",
      optionKeys: ["calldoc_f2_o1", "calldoc_f2_o2", "calldoc_f2_o3"],
      optionAccents: ["red", "amber", "sky"],
    },
    { nameKey: "calldoc_f3_name", type: "number" },
    {
      nameKey: "calldoc_f4_name",
      type: "multi_select",
      optionKeys: ["calldoc_f4_o1", "calldoc_f4_o2", "calldoc_f4_o3"],
      optionAccents: ["red", "amber", "neutral"],
    },
  ],
};

const ROTATION_MS = 5500;

// ============================================================================
// Root Section
// ============================================================================

export const CustomFieldsSection = () => {
  return (
    <section className="dark relative w-full overflow-hidden bg-neutral-950">
      <SectionBackground />
      <div className="relative z-10 w-full max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-20 md:py-28 lg:py-36">
        <BrandReveal />
        <div className="mt-12 lg:mt-20">
          <FieldsScene />
        </div>
        <div className="mt-14 lg:mt-24">
          <CapabilityGrid />
        </div>
      </div>
    </section>
  );
};

// ============================================================================
// Background — dark band with hairlines
// ============================================================================

const SectionBackground = () => (
  <>
    <div className="absolute inset-x-0 top-0 h-px bg-neutral-800" />
    <div className="absolute inset-x-0 bottom-0 h-px bg-neutral-800" />
  </>
);

// ============================================================================
// Block 1 — Brand Reveal
// ============================================================================

const BrandReveal = () => {
  const t = useTranslations("customFields");
  return (
    <div className="flex flex-col items-center text-center">
      <p className="inline-flex items-center gap-1.5 text-sm font-semibold text-red-500">
        <FieldsIcon className="h-3.5 w-3.5" />
        Custom Fields
      </p>

      <h2 className="mt-4 text-3xl md:text-5xl font-bold leading-tight tracking-tight text-white max-w-3xl">
        {t("tagline")}
      </h2>

      <p className="mt-5 text-sm md:text-base text-neutral-400 max-w-2xl leading-relaxed">
        {t("lead")}
      </p>
    </div>
  );
};

// ============================================================================
// Block 2 — Fields Scene
// ============================================================================

const FieldsScene = () => {
  const t = useTranslations("customFields");
  const [active, setActive] = React.useState<EntityKey>("lead");

  React.useEffect(() => {
    const id = setInterval(() => {
      setActive((prev) => {
        const i = ENTITY_KEYS.indexOf(prev);
        return ENTITY_KEYS[(i + 1) % ENTITY_KEYS.length];
      });
    }, ROTATION_MS);
    return () => clearInterval(id);
  }, []);

  return (
    <div className="relative rounded-2xl border border-neutral-800 bg-neutral-950 overflow-hidden">
      <div className="grid grid-cols-1 lg:grid-cols-12 min-h-[620px]">
        <EntityRail active={active} onSelect={setActive} />
        <FieldsEditor active={active} />
      </div>
      <div className="border-t border-neutral-800 bg-neutral-900/40 px-5 py-3 flex items-center gap-2">
        <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
        <span className="text-[10px] text-neutral-500">{t("footer_meta")}</span>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Entity rail (left)
// ----------------------------------------------------------------------------

const EntityRail = ({
  active,
  onSelect,
}: {
  active: EntityKey;
  onSelect: (k: EntityKey) => void;
}) => {
  const t = useTranslations("customFields");
  return (
    <div className="lg:col-span-4 border-b lg:border-b-0 lg:border-r border-neutral-800 bg-neutral-900/40 p-5 flex flex-col">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-1.5">
          <BoltIcon className="h-3.5 w-3.5 text-red-500" />
          <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
            {t("entities_label")}
          </span>
        </div>
        <span className="text-[10px] text-neutral-500 hidden md:inline">
          {t("entities_meta")}
        </span>
      </div>

      <ul className="space-y-2">
        {ENTITY_KEYS.map((key) => {
          const isActive = key === active;
          const fieldCount = ENTITY_FIELDS[key].length;
          return (
            <li key={key}>
              <button
                type="button"
                onClick={() => onSelect(key)}
                className={cn(
                  "w-full text-left rounded-xl border p-3 transition-all",
                  isActive
                    ? "border-red-500/50 bg-neutral-900"
                    : "border-neutral-800 bg-neutral-900/40 hover:border-neutral-700 hover:bg-neutral-900/60"
                )}
              >
                <div className="flex items-center gap-3">
                  <span
                    className={cn(
                      "flex h-3 w-3 flex-shrink-0 items-center justify-center rounded-full",
                      isActive
                        ? "bg-red-500"
                        : "bg-neutral-700"
                    )}
                  >
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <span
                        className={cn(
                          "text-sm font-semibold truncate",
                          isActive ? "text-white" : "text-neutral-300"
                        )}
                      >
                        {t(`ent_${key}_name`)}
                      </span>
                      <span
                        className={cn(
                          "text-[10px] font-mono tabular-nums whitespace-nowrap flex-shrink-0",
                          isActive ? "text-red-300" : "text-neutral-500"
                        )}
                      >
                        {fieldCount}
                      </span>
                    </div>
                    <div className="text-[11px] text-neutral-500 truncate mt-0.5">
                      {t(`ent_${key}_subtitle`)}
                    </div>
                  </div>
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Fields editor (right)
// ----------------------------------------------------------------------------

const FieldsEditor = ({ active }: { active: EntityKey }) => {
  const t = useTranslations("customFields");
  const fields = ENTITY_FIELDS[active];

  return (
    <div className="lg:col-span-8 relative overflow-hidden p-5 bg-neutral-950 flex flex-col">
      {/* Header */}
      <div className="flex items-start justify-between mb-4 gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <FlameIcon className="h-3.5 w-3.5 text-red-500" />
            <span className="text-[10px] font-semibold tracking-wider text-red-400 uppercase">
              {t("fields_for")} {t(`ent_${active}_name`)}
            </span>
          </div>
          <div className="text-[10px] text-neutral-500 mt-0.5">
            {t("fields_meta")}
          </div>
        </div>
        <span className="text-[11px] font-mono tabular-nums text-neutral-400 bg-neutral-900 border border-neutral-800 rounded px-2 py-0.5 whitespace-nowrap">
          {fields.length}
        </span>
      </div>

      {/* Fields list */}
      <AnimatePresence mode="wait">
        <motion.ul
          key={active}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.3 }}
          className="space-y-2 flex-1"
        >
          {fields.map((field, i) => (
            <FieldRow key={field.nameKey} field={field} index={i} />
          ))}
        </motion.ul>
      </AnimatePresence>

      <button
        type="button"
        className="mt-4 self-start text-[11px] font-medium text-neutral-500 hover:text-neutral-300"
      >
        {t("add_field")}
      </button>
    </div>
  );
};

const FieldRow = ({ field, index }: { field: FieldDef; index: number }) => {
  const t = useTranslations("customFields");
  return (
    <motion.li
      initial={{ opacity: 0, x: -4 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.3, delay: index * 0.06 }}
      className="rounded-xl border border-neutral-800 bg-neutral-900/60 p-3"
    >
      <div className="flex items-start gap-3">
        <DragHandleIcon className="h-4 w-4 flex-shrink-0 text-neutral-600 mt-0.5" />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm font-semibold text-neutral-100">
              {t(field.nameKey)}
            </span>
            {field.required && (
              <span
                title={t("required_label")}
                className="text-[10px] uppercase tracking-wider font-semibold text-red-300 bg-red-500/15 ring-1 ring-red-500/30 rounded px-1.5 py-0.5"
              >
                {t("required_label")}
              </span>
            )}
            <span className="text-[10px] uppercase tracking-wider font-semibold text-neutral-400 bg-neutral-800 ring-1 ring-neutral-700 rounded px-1.5 py-0.5">
              {t(`type_${field.type}`)}
            </span>
          </div>
          {field.optionKeys && field.optionAccents && (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {field.optionKeys.map((optKey, i) => (
                <span
                  key={optKey}
                  className={cn(
                    "text-[10px] font-medium rounded-md px-2 py-0.5 ring-1",
                    ACCENT_CLASS[field.optionAccents![i]]
                  )}
                >
                  {t(optKey)}
                </span>
              ))}
            </div>
          )}
          {!field.optionKeys && (
            <FieldPreview type={field.type} />
          )}
        </div>
      </div>
    </motion.li>
  );
};

const FieldPreview = ({ type }: { type: FieldType }) => {
  const previewClass =
    "mt-2 inline-flex items-center gap-1.5 text-[11px] text-neutral-500";
  if (type === "currency") {
    return (
      <div className={previewClass}>
        <span className="font-mono tabular-nums text-neutral-400">€ ___ </span>
        <span className="text-neutral-600">·</span>
        <span>EUR</span>
      </div>
    );
  }
  if (type === "number") {
    return (
      <div className={previewClass}>
        <span className="font-mono tabular-nums text-neutral-400">123 …</span>
      </div>
    );
  }
  if (type === "date") {
    return (
      <div className={previewClass}>
        <span className="font-mono tabular-nums text-neutral-400">
          DD.MM.YYYY
        </span>
      </div>
    );
  }
  if (type === "boolean") {
    return (
      <div className={previewClass}>
        <span className="inline-flex h-4 w-7 items-center rounded-full bg-red-500">
          <span className="ml-[14px] h-3 w-3 rounded-full bg-white" />
        </span>
        <span className="text-neutral-400">true / false</span>
      </div>
    );
  }
  if (type === "url") {
    return (
      <div className={previewClass}>
        <span className="font-mono text-neutral-400">https://…</span>
      </div>
    );
  }
  if (type === "string") {
    return (
      <div className={previewClass}>
        <span className="text-neutral-500">Aa…</span>
      </div>
    );
  }
  return null;
};

// ============================================================================
// Block 3 — Capability Grid
// ============================================================================

type TileConfig = { key: string; icon: React.ReactNode };

const TILES: TileConfig[] = [
  { key: "t1", icon: <IconEntities /> },
  { key: "t2", icon: <IconTypes /> },
  { key: "t3", icon: <IconBrain /> },
  { key: "t4", icon: <IconFilter /> },
];

const CapabilityGrid = () => {
  const t = useTranslations("customFields");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
      {TILES.map((tile) => (
        <CapabilityTile
          key={tile.key}
          icon={tile.icon}
          label={t(`${tile.key}_label`)}
          desc={t(`${tile.key}_desc`)}
        />
      ))}
    </div>
  );
};

const CapabilityTile = ({
  icon,
  label,
  desc,
}: {
  icon: React.ReactNode;
  label: string;
  desc: string;
}) => (
  <div className="relative rounded-xl border border-neutral-800 bg-neutral-900/50 p-5">
    <div className="relative">
      <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-neutral-800 text-red-500">
        {icon}
      </div>
      <h3 className="mt-3 text-sm md:text-base font-semibold text-white">
        {label}
      </h3>
      <p className="mt-1 text-xs md:text-sm text-neutral-400 leading-relaxed">
        {desc}
      </p>
    </div>
  </div>
);

// ============================================================================
// Icons
// ============================================================================

function FlameIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M12 2.5c1.3 3.1 3.8 4.6 4.7 6.9.9 2.3.3 4.7-1.4 6.4-.6.6-1.3 1-2.1 1.2.5-.9.6-2 .2-3-.5-1.2-1.7-1.9-2.9-1.9-.3 1-.9 1.9-1.7 2.5-1.7 1.3-2.3 3.5-1.6 5.4.5 1.4 1.6 2.6 3 3-3.7-.2-7-3.1-7.4-6.9-.4-4.1 2.4-7.7 5.5-10.1C10.3 4.7 11.4 3.6 12 2.5z" />
    </svg>
  );
}

function BoltIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <path d="M13 2L4 14h6l-1 8 9-12h-6l1-8z" />
    </svg>
  );
}

function FieldsIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden
    >
      <rect x="3" y="6" width="18" height="4" rx="1" />
      <rect x="3" y="14" width="14" height="4" rx="1" />
    </svg>
  );
}

function DragHandleIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="currentColor"
      className={className}
      aria-hidden
    >
      <circle cx="9" cy="6" r="1.4" />
      <circle cx="15" cy="6" r="1.4" />
      <circle cx="9" cy="12" r="1.4" />
      <circle cx="15" cy="12" r="1.4" />
      <circle cx="9" cy="18" r="1.4" />
      <circle cx="15" cy="18" r="1.4" />
    </svg>
  );
}

function IconEntities() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <rect x="3" y="3" width="7" height="7" rx="1" />
      <rect x="14" y="3" width="7" height="7" rx="1" />
      <rect x="3" y="14" width="7" height="7" rx="1" />
      <rect x="14" y="14" width="7" height="7" rx="1" />
    </svg>
  );
}

function IconTypes() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M4 6h6M4 10h12M4 14h8M4 18h14" />
    </svg>
  );
}

function IconBrain() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M9 4a3 3 0 0 0-3 3v1a3 3 0 0 0-2 5 3 3 0 0 0 1 4 3 3 0 0 0 4 2 3 3 0 0 0 6 0 3 3 0 0 0 4-2 3 3 0 0 0 1-4 3 3 0 0 0-2-5V7a3 3 0 0 0-3-3 3 3 0 0 0-3 1 3 3 0 0 0-3-1z" />
      <path d="M12 5v14" />
    </svg>
  );
}

function IconFilter() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      className="h-5 w-5"
      aria-hidden
    >
      <path d="M3 5h18l-7 9v6l-4-2v-4z" />
    </svg>
  );
}
